import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Account } from '../types';
import { addFavorite, getAccounts, getFavorites, removeFavorite } from '../lib/api';
import { buildQueryString, countActiveFilters, emptyFilters, type Filters } from '../lib/filterQuery';
import AccountCard from '../components/AccountCard';
import SkeletonCard from '../components/SkeletonCard';
import EmptyState from '../components/EmptyState';
import FilterSheet from '../components/FilterSheet';
import { hapticImpact, hapticNotification } from '../lib/telegram';
import { useDragScroll } from '../hooks/useDragScroll';

const QUICK_FILTERS: { label: string; apply: (f: Filters) => Filters; match: (f: Filters) => boolean }[] = [
  { label: 'Все', apply: () => emptyFilters, match: (f) => countActiveFilters(f) === 0 },
  {
    label: '👑 С Legend',
    apply: (f) => ({ ...f, legendMin: f.legendMin ? undefined : 1 }),
    match: (f) => !!f.legendMin,
  },
  { label: 'KOF', apply: (f) => toggleCollabQuick(f, 'kof'), match: (f) => f.collabs.includes('kof') },
  { label: 'Naruto', apply: (f) => toggleCollabQuick(f, 'naruto'), match: (f) => f.collabs.includes('naruto') },
  {
    label: 'JJK',
    apply: (f) => toggleCollabQuick(f, 'jujutsu_kaisen'),
    match: (f) => f.collabs.includes('jujutsu_kaisen'),
  },
  {
    label: 'Дешевле $50',
    apply: (f) => ({ ...f, priceMax: f.priceMax === 50 ? undefined : 50 }),
    match: (f) => f.priceMax === 50,
  },
];

function toggleCollabQuick(f: Filters, id: string): Filters {
  return { ...f, collabs: f.collabs.includes(id) ? f.collabs.filter((c) => c !== id) : [...f.collabs, id] };
}

const SORT_OPTIONS: { value: Filters['sort']; label: string }[] = [
  { value: 'newest', label: 'Новые' },
  { value: 'price_asc', label: 'Цена ↑' },
  { value: 'price_desc', label: 'Цена ↓' },
];

export default function CatalogPage() {
  const [filters, setFilters] = useState<Filters>(emptyFilters);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const [searchInput, setSearchInput] = useState('');

  const sentinelRef = useRef<HTMLDivElement>(null);
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const touchStartY = useRef<number | null>(null);
  const PULL_THRESHOLD = 70;

  const fetchFirstPage = useCallback(async (f: Filters) => {
    setLoading(true);
    try {
      const res = await getAccounts(buildQueryString(f, { limit: 20 }));
      setAccounts(res.items);
      setCursor(res.nextCursor);
      setHasMore(Boolean(res.nextCursor));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFirstPage(filters);
  }, [filters, fetchFirstPage]);

  useEffect(() => {
    getFavorites()
      .then((favs) => setFavorites(new Set(favs.map((a) => a.id))))
      .catch(() => {});
  }, []);

  const loadMore = useCallback(async () => {
    if (!hasMore || loadingMore || !cursor) return;
    setLoadingMore(true);
    try {
      const res = await getAccounts(buildQueryString(filters, { limit: 20, cursor }));
      setAccounts((prev) => [...prev, ...res.items]);
      setCursor(res.nextCursor);
      setHasMore(Boolean(res.nextCursor));
    } finally {
      setLoadingMore(false);
    }
  }, [filters, cursor, hasMore, loadingMore]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMore();
      },
      { rootMargin: '400px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loadMore]);

  // Debounced search -> filters.q
  useEffect(() => {
    const t = setTimeout(() => {
      setFilters((f) => (f.q === searchInput ? f : { ...f, q: searchInput || undefined }));
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const toggleFavorite = async (id: string) => {
    const isFav = favorites.has(id);
    setFavorites((prev) => {
      const next = new Set(prev);
      if (isFav) next.delete(id);
      else next.add(id);
      return next;
    });
    try {
      if (isFav) await removeFavorite(id);
      else await addFavorite(id);
      hapticNotification('success');
    } catch {
      hapticNotification('error');
    }
  };

  const activeFilterCount = useMemo(() => countActiveFilters(filters), [filters]);
  const quickFiltersDrag = useDragScroll<HTMLDivElement>();

  const handleTouchStart = (e: React.TouchEvent) => {
    if (window.scrollY === 0) touchStartY.current = e.touches[0]!.clientY;
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchStartY.current === null || refreshing) return;
    const delta = e.touches[0]!.clientY - touchStartY.current;
    if (delta > 0) setPullDistance(Math.min(delta, 120));
  };
  const handleTouchEnd = async () => {
    if (pullDistance > PULL_THRESHOLD) {
      hapticImpact('medium');
      setRefreshing(true);
      await fetchFirstPage(filters);
      setRefreshing(false);
    }
    setPullDistance(0);
    touchStartY.current = null;
  };

  return (
    <div className="pb-20" onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>
      {(pullDistance > 0 || refreshing) && (
        <div
          className="flex items-center justify-center overflow-hidden text-sm text-white/50 transition-all"
          style={{ height: refreshing ? 40 : pullDistance }}
        >
          {refreshing ? '🔄 Обновление...' : pullDistance > PULL_THRESHOLD ? '↑ Отпусти для обновления' : '↓ Потяни для обновления'}
        </div>
      )}
      <header className="sticky top-0 z-30 border-b border-mlbb-border bg-mlbb-bg/95 px-4 pb-3 pt-4 backdrop-blur-sm">
        <div className="mb-3 flex items-center gap-2">
          <span className="text-lg font-bold text-white">🛡️ ARC TEAM</span>
          <span className="text-xs text-white/40">MLBB Marketplace</span>
        </div>
        <input
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Поиск по скину или герою..."
          className="w-full rounded-full border border-mlbb-border bg-mlbb-card px-4 py-2.5 text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-1 focus:ring-mlbb-accent-from"
        />
        <div
          ref={quickFiltersDrag.ref}
          onMouseDown={quickFiltersDrag.onMouseDown}
          onMouseMove={quickFiltersDrag.onMouseMove}
          onMouseUp={quickFiltersDrag.onMouseUp}
          onMouseLeave={quickFiltersDrag.onMouseLeave}
          onClickCapture={quickFiltersDrag.onClickCapture}
          className={`mt-3 flex items-center gap-2 overflow-x-auto pb-1 ${quickFiltersDrag.className}`}
          style={{ scrollbarWidth: 'none' }}
        >
          {QUICK_FILTERS.map((qf) => (
            <button
              key={qf.label}
              onClick={() => {
                hapticImpact('light');
                setFilters((f) => qf.apply(f));
              }}
              className={`shrink-0 whitespace-nowrap rounded-full border px-3 py-1.5 text-sm ${
                qf.match(filters)
                  ? 'border-transparent bg-mlbb-accent text-white'
                  : 'border-mlbb-border bg-mlbb-card text-white/70'
              }`}
            >
              {qf.label}
            </button>
          ))}
        </div>
        <div className="mt-3 flex items-center justify-between">
          <button
            onClick={() => setSheetOpen(true)}
            className="relative flex items-center gap-1.5 rounded-full border border-mlbb-border bg-mlbb-card px-3 py-1.5 text-sm text-white"
          >
            ⚙️ Фильтры
            {activeFilterCount > 0 && (
              <span className="flex h-4 w-4 items-center justify-center rounded-full bg-mlbb-accent-from text-[10px] text-white">
                {activeFilterCount}
              </span>
            )}
          </button>
          <select
            value={filters.sort}
            onChange={(e) => setFilters((f) => ({ ...f, sort: e.target.value as Filters['sort'] }))}
            className="rounded-full border border-mlbb-border bg-mlbb-card px-3 py-1.5 text-sm text-white"
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </header>

      <div className="grid grid-cols-1 gap-3 p-4">
        {loading ? (
          Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} />)
        ) : accounts.length === 0 ? (
          <EmptyState
            title="Ничего не найдено"
            subtitle="Попробуй ослабить фильтры"
            actionLabel={activeFilterCount > 0 ? 'Сбросить фильтры' : undefined}
            onAction={() => setFilters(emptyFilters)}
          />
        ) : (
          accounts.map((a) => (
            <AccountCard key={a.id} account={a} favorite={favorites.has(a.id)} onToggleFavorite={toggleFavorite} />
          ))
        )}
      </div>

      <div ref={sentinelRef} className="h-4" />
      {loadingMore && (
        <div className="grid grid-cols-1 gap-3 p-4 pt-0">
          <SkeletonCard />
        </div>
      )}

      <FilterSheet
        open={sheetOpen}
        initial={filters}
        onClose={() => setSheetOpen(false)}
        onApply={(f) => {
          setFilters(f);
          setSheetOpen(false);
        }}
      />
    </div>
  );
}
